import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import { setSection, updateSection } from "../../../assignmentSlice";
import { getContentHeight } from "../../../../../utils/textUtils";
import {
  levelOfCategory,
  readingThemeOf,
  readingTypeName,
  splitReadingExplanation,
} from "../basic_choice/KoreanReading";
import "../basic_choice/KoreanReading.css";
import "./ReadingBookPage.css";

// 문제뱅크 문해력을 전자책으로(2026-10-18 대표: "전자책처럼, 리더스북처럼").
// 지문 하나가 한 단원이다. 왼쪽은 여는 쪽(수준 색 띠 · Unit 탭 · 크림 지문 카드), 오른쪽은 그 지문의 문항 쪽.
// 아이는 종이 위 선지를 눌러 연필 동그라미를 치고, 펼친 면에서 '채점하기' 를 누르면 같은 종이 위에 빨간 펜으로
// 첨삭이 얹힌다(정답 번호 동그라미 · 고른 오답 빗금 · 오답 이유 · 정답 해설). 지인에듀 '문해력 국어 디자인 문제집판' 참고.
//
// 저장은 어법 화면(GrammarPage)과 같은 문항 목록이다: 문항마다 input(1부터) · isCorrect · isSubmitted.
// 채점 전에 고른 것은 draft_input 에 둔다. 그래서 점수 · 리포트 · 해설지가 그대로 돈다.

const CIRCLED_NUMBER = "①②③④⑤⑥⑦⑧⑨⑩";
const HAND_CIRCLE_PATH = "M20 4 C31 3 37 11 36 21 C35 31 27 37 18 36 C9 35 3 27 4 18 C5 10 12 5 22 5";
const HAND_SLASH_PATH = "M8 34 C16 24 24 14 34 4";
const LEVEL_NAME = { 1: "초등", 2: "초등", 3: "초등", 4: "중등", 5: "중등", 6: "중등", 7: "고등", 8: "고등", 9: "고등" };

const isNarrowScreen = () => typeof window !== "undefined" && Math.min(window.innerWidth, window.screen.width) <= 768;
const isSpreadScreen = () =>
  typeof window !== "undefined" && window.innerWidth >= 1000 && window.innerWidth > window.innerHeight * 1.15;

const stripLeadingNumber = (text) => String(text || "").replace(/^\s*(?:[①-⑩]|\(?\d{1,2}[).])\s*/, "");

// 문항 목록 → 단원(지문 하나와 그 문항들). 지문은 첫 문항에만 실려 올 수 있어 같은 지문끼리 잇는다.
const buildUnitList = (pageList) => {
  const unitList = [];
  pageList.forEach((page, itemIndex) => {
    const last = unitList[unitList.length - 1];
    if (!last || (page.passage && page.passage !== last.passage)) {
      unitList.push({ passage: page.passage || "", category: page.category, itemIndexList: [itemIndex] });
    } else {
      last.itemIndexList.push(itemIndex);
    }
  });
  return unitList;
};

const HandMark = ({ path, color = "#D6402E", size = "1.9em" }) => (
  <svg className="rb-hand" viewBox="0 0 40 40" style={{ width: size, height: size }}>
    <path d={path} stroke={color} />
  </svg>
);

const PassagePage = ({ unit, unitNumber, theme }) => {
  const level = levelOfCategory(unit.category);
  const blockList = String(unit.passage || "")
    .split(/\n\s*\n/)
    .map((block) => block.trim())
    .filter(Boolean);
  let paragraphNumber = 0;
  return (
    <div className="rb-paper rb-paper--passage">
      <div className="rb-band" style={{ background: `linear-gradient(135deg, ${theme.color}, ${theme.sub})` }}>
        <span className="rb-unit-tab">
          Unit
          <b>{String(unitNumber).padStart(2, "0")}</b>
        </span>
        <span className="rb-band-pill">{LEVEL_NAME[level] || ""} 국어 독해</span>
      </div>
      <div className="rb-passage-card">
        {blockList.map((block, blockIndex) => {
          const isTable = block.split("\n").filter((line) => line.includes("|")).length >= 2;
          if (isTable) {
            const rowList = block.split("\n").filter((line) => line.includes("|")).map((line) => line.split("|").map((cell) => cell.trim()));
            return (
              <table className="rb-table" key={blockIndex}>
                <thead>
                  <tr>
                    {rowList[0].map((cell, index) => (
                      <th key={index} style={{ background: theme.soft, borderBottomColor: theme.color }}>{cell}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rowList.slice(1).map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {row.map((cell, cellIndex) => (
                        <td key={cellIndex}>{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            );
          }
          paragraphNumber += 1;
          return (
            <div className="rb-paragraph" key={blockIndex}>
              <span className="rb-paragraph-number" style={{ color: theme.color }}>
                {paragraphNumber}
                <HandMark path={HAND_CIRCLE_PATH} color={theme.color} size="1.75em" />
              </span>
              <p>{block}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const QuestionPage = ({ unit, pageList, theme, firstNumber, onPick, isLocked }) => (
  <div className="rb-paper rb-paper--question">
    <div className="rb-section-title">
      <span className="rb-section-number" style={{ borderColor: theme.color, color: theme.color }}>1</span>
      <span className="rb-highlight">글 내용 살피기</span>
    </div>
    {unit.itemIndexList.map((itemIndex, order) => {
      const page = pageList[itemIndex];
      const number = firstNumber + order;
      const typeName = readingTypeName(page.category);
      const explanation = splitReadingExplanation(page.explanation);
      const picked = page.isSubmitted ? page.input : page.draft_input;
      return (
        <div className="rb-item" key={itemIndex}>
          <div className="rb-item-column">
            <span className="rb-item-number" style={{ color: theme.color }}>{number}</span>
            {typeName && (
              <span className="rb-item-type" style={{ color: theme.color }}>
                {typeName.match(/.{1,2}/g).join("\n")}
              </span>
            )}
          </div>
          <div className="rb-item-body">
            <div className="rb-item-question">{page.question}</div>
            <div className="rb-choice-list">
              {(page.choices || []).map((choice, choiceIndex) => {
                const choiceNumber = choiceIndex + 1;
                const isPicked = picked === choiceNumber;
                const isAnswer = page.isSubmitted && choiceNumber === page.answer;
                const isWrongPick = page.isSubmitted && isPicked && !isAnswer;
                const reason = page.isSubmitted && !isAnswer ? explanation.reasonByIndex[choiceIndex] : "";
                return (
                  <div key={choiceIndex} className={`rb-choice${page.isSubmitted && !isAnswer && !isWrongPick ? " is-dim" : ""}`}>
                    <button
                      type="button"
                      className="rb-choice-line"
                      disabled={isLocked || page.isSubmitted}
                      onClick={() => onPick(itemIndex, choiceNumber)}
                    >
                      <span className="rb-choice-number" style={{ color: isAnswer ? "#D6402E" : theme.color }}>
                        {CIRCLED_NUMBER[choiceIndex]}
                        {/* 채점 전 고른 것은 연필 동그라미 */}
                        {isPicked && !page.isSubmitted && <HandMark path={HAND_CIRCLE_PATH} color="#3B4250" />}
                        {isAnswer && <HandMark path={HAND_CIRCLE_PATH} />}
                        {isWrongPick && <HandMark path={HAND_SLASH_PATH} size="1.6em" />}
                      </span>
                      <span className="rb-choice-text">{stripLeadingNumber(choice)}</span>
                      {isAnswer && <span className="rb-check">✓</span>}
                    </button>
                    {reason && <div className="rb-reason">{reason}</div>}
                  </div>
                );
              })}
            </div>
            {page.isSubmitted && explanation.main && (
              <div className="rb-explain">
                <span className="rb-explain-head">{page.isCorrect ? "정답!" : "정답은 " + CIRCLED_NUMBER[page.answer - 1]}</span>
                {explanation.main}
              </div>
            )}
          </div>
        </div>
      );
    })}
  </div>
);

const ReadingBookPage = ({ section, pageList, setPageList, setCurrentStep, isPreview, isReview }) => {
  const dispatch = useDispatch();
  const unitList = useMemo(() => buildUnitList(pageList), [pageList]);
  const theme = readingThemeOf(pageList[0]?.category);
  const [isMobile, setIsMobile] = useState(isNarrowScreen);
  const [isSpread, setIsSpread] = useState(isSpreadScreen);
  const stageRef = useRef(null);

  // 쪽 차례: 단원마다 [여는 쪽, 문항 쪽]
  const sheetList = useMemo(
    () => unitList.flatMap((unit, unitIndex) => [
      { kind: "passage", unitIndex },
      { kind: "question", unitIndex },
    ]),
    [unitList]
  );
  // 넓은 화면은 한 단원(두 쪽)을 펼쳐 놓는다
  const spreadList = useMemo(
    () => (isSpread ? unitList.map((_, unitIndex) => [unitIndex * 2, unitIndex * 2 + 1]) : sheetList.map((_, index) => [index])),
    [isSpread, unitList, sheetList]
  );
  const firstOpenUnit = Math.max(0, unitList.findIndex((unit) => unit.itemIndexList.some((index) => !pageList[index].isSubmitted)));
  const [spreadIndex, setSpreadIndex] = useState(() => (isSpreadScreen() ? firstOpenUnit : firstOpenUnit * 2));

  useEffect(() => {
    const onResize = () => {
      const nextSpread = isSpreadScreen();
      setIsMobile(isNarrowScreen());
      setIsSpread((previous) => {
        if (previous !== nextSpread) setSpreadIndex((index) => (nextSpread ? Math.floor(index / 2) : index * 2));
        return nextSpread;
      });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (stageRef.current) stageRef.current.scrollTop = 0;
  }, [spreadIndex]);

  const currentSheetList = (spreadList[spreadIndex] || []).map((index) => sheetList[index]).filter(Boolean);
  const currentUnitIndexList = [...new Set(currentSheetList.map((sheet) => sheet.unitIndex))];
  const currentItemIndexList = currentUnitIndexList.flatMap((unitIndex) => unitList[unitIndex].itemIndexList);
  const hasQuestionSheet = currentSheetList.some((sheet) => sheet.kind === "question");
  const isGraded = currentItemIndexList.every((index) => pageList[index].isSubmitted);
  const isLast = spreadIndex >= spreadList.length - 1;

  const persist = (nextPageList, isFinished) => {
    setPageList(nextPageList);
    dispatch(setSection({ ...section, answer: nextPageList }));
    if (isPreview || isReview || !section.id) return;
    dispatch(updateSection({ sectionId: section.id, data: isFinished ? { is_finished: true, answer: nextPageList } : { answer: nextPageList } }));
  };

  const pick = (itemIndex, choiceNumber) => {
    persist(pageList.map((page, index) => (index === itemIndex ? { ...page, draft_input: choiceNumber } : page)), false);
  };

  const gradeCurrent = () => {
    const unpicked = currentItemIndexList.filter((index) => !pageList[index].isSubmitted && !pageList[index].draft_input);
    if (unpicked.length) {
      alert(`${unpicked.map((index) => index + 1).join(", ")}번을 아직 안 골랐어요.`);
      return;
    }
    persist(
      pageList.map((page, index) =>
        currentItemIndexList.includes(index) && !page.isSubmitted
          ? { ...page, input: page.draft_input, isCorrect: page.draft_input === page.answer, isSubmitted: true }
          : page
      ),
      false
    );
  };

  const finish = () => {
    if (isReview) {
      setCurrentStep(2);
      return;
    }
    const remaining = pageList.filter((page) => !page.isSubmitted).length;
    if (remaining > 0) {
      alert(`아직 채점하지 않은 문제가 ${remaining}개 있어요.`);
      return;
    }
    persist(pageList, true);
    alert("학습이 완료되었습니다.");
    setCurrentStep(2);
  };

  const goTo = (index) => setSpreadIndex(Math.min(Math.max(index, 0), spreadList.length - 1));

  const touchStartRef = useRef(null);
  const onTouchStart = (event) => {
    touchStartRef.current = { x: event.touches[0].clientX, y: event.touches[0].clientY };
  };
  const onTouchEnd = (event) => {
    const start = touchStartRef.current;
    touchStartRef.current = null;
    if (!start) return;
    const distanceX = event.changedTouches[0].clientX - start.x;
    const distanceY = event.changedTouches[0].clientY - start.y;
    if (Math.abs(distanceX) < 60 || Math.abs(distanceX) < Math.abs(distanceY) * 1.5) return;
    goTo(spreadIndex + (distanceX < 0 ? 1 : -1));
  };

  const scoreText = (() => {
    const graded = currentItemIndexList.filter((index) => pageList[index].isSubmitted);
    if (!hasQuestionSheet || graded.length !== currentItemIndexList.length || !graded.length) return "";
    return `${graded.filter((index) => pageList[index].isCorrect).length} / ${graded.length}`;
  })();

  return (
    <div className="rb" style={{ height: getContentHeight(isMobile), "--rb-theme": theme.color, "--rb-soft": theme.soft }}>
      <div className="rb-stage" ref={stageRef} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <div className={`rb-spread${currentSheetList.length > 1 ? " rb-spread--double" : ""}`}>
          {currentSheetList.map((sheet) => {
            const unit = unitList[sheet.unitIndex];
            const firstNumber = unit.itemIndexList[0] + 1;
            return (
              <div className="rb-sheet" key={`${sheet.kind}-${sheet.unitIndex}`}>
                {sheet.kind === "passage" ? (
                  <PassagePage unit={unit} unitNumber={sheet.unitIndex + 1} theme={readingThemeOf(unit.category)} />
                ) : (
                  <QuestionPage
                    unit={unit}
                    pageList={pageList}
                    theme={readingThemeOf(unit.category)}
                    firstNumber={firstNumber}
                    onPick={pick}
                    isLocked={isReview}
                  />
                )}
                {sheet.kind === "question" && scoreText && <div className="rb-score">{scoreText}</div>}
              </div>
            );
          })}
        </div>
      </div>
      <div className="rb-foot">
        <button type="button" className="rb-turn" onClick={() => goTo(spreadIndex - 1)} disabled={spreadIndex === 0} aria-label="앞 쪽">
          ‹
        </button>
        <div className="rb-progress">
          <div style={{ width: `${((spreadIndex + 1) / spreadList.length) * 100}%` }} />
        </div>
        {hasQuestionSheet && !isGraded && !isReview && (
          <button type="button" className="rb-grade" onClick={gradeCurrent}>
            채점하기
          </button>
        )}
        {isLast && (isGraded || isReview) && (
          <button type="button" className="rb-finish" onClick={finish}>
            {isReview ? "나가기" : "다 풀었어요"}
          </button>
        )}
        <button type="button" className="rb-turn" onClick={() => goTo(spreadIndex + 1)} disabled={isLast} aria-label="다음 쪽">
          ›
        </button>
      </div>
    </div>
  );
};

export default ReadingBookPage;
